import React, { useState, useEffect, useRef } from 'react';

interface StatItemProps {
  value: number;
  label: string;
  suffix?: string;
  duration?: number;
}

const StatItem: React.FC<StatItemProps> = ({ value, label, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef<number>(0);
  const startTimeRef = useRef<number | null>(null);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    // Animation function
    const animate = (timestamp: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }

      const progress = timestamp - startTimeRef.current;
      const progressRatio = Math.min(progress / duration, 1);

      // Easing function (ease-out-cubic)
      const eased = 1 - Math.pow(1 - progressRatio, 3);

      // Calculate the current count based on progress
      countRef.current = Math.floor(eased * value);
      setCount(countRef.current);

      // Continue animation if not complete
      if (progressRatio < 1) {
        frameRef.current = requestAnimationFrame(animate);
      }
    };

    // Start the animation
    frameRef.current = requestAnimationFrame(animate);

    // Cleanup on unmount
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [value, duration]);

  return (
    <div className="text-center p-6 rounded-xl bg-[#211A29]/50 backdrop-blur-sm border border-white/5 transition-all duration-300 hover:bg-[#211A29]/80 hover:translate-y-[-5px] hover:shadow-[0_15px_30px_-10px_rgba(123,_31,_162,_0.2)]">
      <div className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#7F56D9] to-[#F670C7]">
        {count.toLocaleString()}{suffix}
      </div>
      <div className="text-[#A3A3A3] mt-2 font-medium">{label}</div>
    </div>
  );
};

const Stats: React.FC = () => {
  return (
    <div className="container mx-auto px-4 relative z-10">
      <div className="text-center mb-12">
        <span className="inline-block px-3 py-1 rounded-full bg-[#7F56D9]/20 text-[#7F56D9] font-medium text-sm mb-4">
          Our Impact
        </span>
        <h2 className="text-3xl font-bold mb-4">Developer Marketplace in Numbers</h2>
        <p className="text-[#A3A3A3] max-w-2xl mx-auto">Join our thriving community of creators and developers building the future of digital content.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        <StatItem value={25000} label="Active Users" suffix="+" />
        <StatItem value={13450} label="Products" />
        <StatItem value={4800000} label="Downloads" />
        <StatItem value={98} label="Satisfaction Rate" suffix="%" />
      </div>
    </div>
  );
};

export default Stats;
